import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, get, patch, post, put } from './api';
import { Modal } from './ui';

/**
 * "<Name>’s availability" (prototype Employees → Availability). Managers of the store and admins paint
 * Preferred / Available / Not available; each day saves when the mouse or finger is let go, and the
 * employee gets a notice saying who changed it and when. The weekly max hours and days are set here too.
 * HR sees it read-only. The grid shows 9 AM – midnight, like the prototype.
 */

export const DAY_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const FIRST = 16; const LAST = 76;   // quarter-hours from 5 AM: 9 AM .. midnight
type Mark = '.' | 'P' | 'N';

export const MAX_H_OPTS: [string, string][] = [['', 'No limit'], ...[8, 12, 16, 20, 24, 25, 28, 29, 30, 32, 35, 36, 40].map((h): [string, string] => [String(h), `${h} hours`])];
export const MAX_D_OPTS: [string, string][] = [['', 'No limit'], ...[1, 2, 3, 4, 5, 6].map((d): [string, string] => [String(d), `${d} day${d === 1 ? '' : 's'}`])];

export function limitText(h: number | null, d: number | null): string {
  if (h == null && d == null) return 'No weekly limit set';
  return 'Wants at most ' + [h != null ? `${h} h` : '', d != null ? `${d} day${d === 1 ? '' : 's'}` : ''].filter(Boolean).join(' and ') + ' a week';
}

function hourLabel(H: number) { const h = H % 24; return `${h % 12 === 0 ? 12 : h % 12}${h >= 12 ? 'PM' : 'AM'}`; }
function tlabel(b: number) {
  const m = 300 + 15 * b; const h = Math.floor(m / 60) % 24; const mm = m % 60;
  return `${h % 12 === 0 ? 12 : h % 12}:${mm < 10 ? '0' : ''}${mm}${h >= 12 ? 'p' : 'a'}`;
}

interface Resp { name: string; firstName: string; days: string[]; maxHours: number | null; maxDays: number | null; canEdit: boolean }

const BRUSHES: [Mark, string, string][] = [
  ['P', 'Preferred', 'Times they’d like to work'],
  ['.', 'Available', 'Times they can work'],
  ['N', 'Not available', 'Times they can’t work'],
];

export function AvailabilityModal<E>({ employeeId, onClose, onChange }: {
  employeeId: number; onClose: () => void; onChange: (e: E) => void;
}) {
  const [d, setD] = useState<Resp | null>(null);
  const [err, setErr] = useState('');
  const [brush, setBrush] = useState<Mark>('P');
  const paint = useRef<{ wd: number; last: number; val: Mark } | null>(null);
  const daysRef = useRef<string[]>([]);
  const saving = useRef(Promise.resolve());

  useEffect(() => {
    get<Resp>(`/api/employees/${employeeId}/availability`)
      .then((r) => { daysRef.current = r.days; setD(r); })
      .catch((e) => setErr(e instanceof ApiError ? e.message : 'Can’t load availability.'));
  }, [employeeId]);

  const setRange = (wd: number, a: number, b: number, val: Mark) => {
    const row = daysRef.current[wd]!.split('');
    for (let k = Math.min(a, b); k <= Math.max(a, b); k++) row[k] = val;
    daysRef.current = daysRef.current.map((x, i) => (i === wd ? row.join('') : x));
    setD((cur) => cur && { ...cur, days: daysRef.current });
  };

  // Let go: save the day that was painted (one save per stroke, in order).
  const finish = useCallback(() => {
    const p = paint.current; if (!p) return;
    paint.current = null;
    const blocks = daysRef.current[p.wd]!;
    saving.current = saving.current.then(async () => {
      try { onChange((await put<{ employee: E }>(`/api/employees/${employeeId}/availability`, { weekday: p.wd, blocks })).employee); setErr(''); }
      catch (e) { setErr(e instanceof ApiError ? e.message : 'That didn’t save. Check your connection and paint it again.'); }
    });
  }, [employeeId, onChange]);
  useEffect(() => {
    window.addEventListener('pointerup', finish);
    window.addEventListener('pointercancel', finish);
    return () => { window.removeEventListener('pointerup', finish); window.removeEventListener('pointercancel', finish); };
  }, [finish]);

  const cellAt = (x: number, y: number) => {
    const el = document.elementFromPoint(x, y) as HTMLElement | null;
    if (!el?.dataset.wd) return null;
    return { wd: Number(el.dataset.wd), b: Number(el.dataset.b) };
  };

  const setLimit = async (field: 'maxHours' | 'maxDays', v: string) => {
    try {
      const r = await patch<{ employee: E & { maxHours: number | null; maxDays: number | null } }>(`/api/employees/${employeeId}`, { [field]: v === '' ? null : Number(v) });
      onChange(r.employee);
      setD((cur) => cur && { ...cur, maxHours: r.employee.maxHours, maxDays: r.employee.maxDays });
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'That didn’t save. Try again.'); }
  };

  const edit = !!d?.canEdit;
  return (
    <Modal title={d ? `${d.name}’s availability` : 'Availability'} onClose={onClose} wide>
      {!d && !err && <div style={{ fontSize: 14, color: '#4A5561' }}>Loading…</div>}
      {d && edit && (
        <div className="emlimits"><span style={{ fontSize: 13, fontWeight: 600, color: '#17212B' }}>Most they want to work each week</span>
          <label>Hours<select className="sel-sm" value={d.maxHours != null ? String(d.maxHours) : ''} onChange={(e) => void setLimit('maxHours', e.target.value)} aria-label="Most hours a week for this employee">
            {MAX_H_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
          <label>Days<select className="sel-sm" value={d.maxDays != null ? String(d.maxDays) : ''} onChange={(e) => void setLimit('maxDays', e.target.value)} aria-label="Most days a week for this employee">
            {MAX_D_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
        </div>
      )}
      {d && edit && (
        <div className="brushrow" role="group" aria-label="What to paint"><span style={{ fontSize: 13, color: '#4A5561' }}>Click or drag to mark times as</span>
          {BRUSHES.map(([v, label, hint]) => (
            <button key={v} className={'brush b' + (v === '.' ? 'A' : v) + (brush === v ? ' on' : '')} onClick={() => setBrush(v)} aria-pressed={brush === v} title={hint}>{label}</button>
          ))}
          <span style={{ fontSize: 12, color: '#4A5561', flexBasis: '100%' }}>Start on a time that’s already marked that way to clear it again.</span>
        </div>
      )}
      {d && (
        <div className="agrid-wrap" style={{ touchAction: edit ? 'none' : undefined }}
          onPointerMove={(ev) => {
            const p = paint.current; if (!p) return;
            const c = cellAt(ev.clientX, ev.clientY);
            if (!c || c.wd !== p.wd || c.b === p.last) return;
            setRange(p.wd, p.last, c.b, p.val); p.last = c.b;
          }}>
          <div className="agrid">
            <div className="ahead"><span />{Array.from({ length: 15 }, (_, i) => <span key={i} className="ah">{hourLabel(9 + i)}</span>)}</div>
            {DAY_FULL.map((day, wd) => (
              <div key={wd} className="arow"><span className="aday">{day}</span>
                {Array.from({ length: LAST - FIRST }, (_, i) => {
                  const b = FIRST + i; const v = d.days[wd]![b] as Mark;
                  return (
                    <button key={b} className={'acell a' + (v === '.' ? 'A' : v) + (b % 4 === 0 ? ' hr' : '')} disabled={!edit} data-wd={wd} data-b={b}
                      aria-label={`${day} ${tlabel(b)}: ${v === 'P' ? 'preferred' : v === 'N' ? 'not available' : 'available'}`}
                      onPointerDown={(ev) => {
                        if (!edit) return;
                        ev.preventDefault();
                        (ev.target as HTMLElement).releasePointerCapture?.(ev.pointerId);
                        // like the day board: starting on a time that already has this mark clears it back to Available
                        const val: Mark = brush !== '.' && v === brush ? '.' : brush;
                        paint.current = { wd, last: b, val };
                        setRange(wd, b, b, val);
                      }}
                      onKeyDown={(ev) => { if (edit && (ev.key === 'Enter' || ev.key === ' ')) { ev.preventDefault(); const val: Mark = brush !== '.' && v === brush ? '.' : brush; paint.current = { wd, last: b, val }; setRange(wd, b, b, val); finish(); } }} />
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
      {err && <div className="fieldnote bad" style={{ fontSize: 13 }}>{err}</div>}
      {d && (
        <div style={{ fontSize: 13, lineHeight: 1.5, color: '#4A5561' }}>
          {edit ? `Changes save as you paint, and ${d.firstName} gets a notice in their app saying who changed it and when. ` : 'Set by the employee. '}
          {limitText(d.maxHours, d.maxDays)}. Green = preferred, white = available, red = not available. The day board shows the same shading on that person’s row.
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}><button className="btn2" onClick={onClose}>Close</button></div>
    </Modal>
  );
}

/** The notice an employee sees when a manager or admin changed their availability, with Dismiss. */
export function AvailNotice() {
  const [text, setText] = useState('');
  useEffect(() => {
    get<{ notice: { text: string } | null }>('/api/me/availability').then((r) => setText(r.notice?.text ?? '')).catch(() => setText(''));
  }, []);
  if (!text) return null;
  return (
    <div className="avnotice" role="status"><span>{text}</span>
      <button className="btn2" style={{ minHeight: 32, padding: '2px 12px', flexShrink: 0 }} onClick={() => { setText(''); void post('/api/me/availability/notice/dismiss'); }}>Dismiss</button>
    </div>
  );
}
